/**
 * The email layout, console side: its palette rule, its default, and a preview renderer.
 *
 * ## Why any of this is duplicated
 *
 * All three of these have an authority elsewhere. The palette rule is
 * `checkEmailPaletteLegibility` in `packages/contracts/src/email-brand.ts`, which the API runs
 * through the schema on every save. The default is `DEFAULT_EMAIL_LAYOUT` in the same file. The
 * renderer is `renderWithLayout` in `apps/api/src/modules/email/layout.ts`, and what it produces
 * is what actually leaves the building.
 *
 * They are mirrored here rather than imported for the reason `bannerContrast.ts` sets out at
 * length: this console imports `@genex/contracts` for **types only**. The tsconfig alias points
 * at the built `.d.ts` and Vite is deliberately never told about it, so the console builds with
 * no workspace build and ships none of the contract package's runtime dependencies. Importing
 * one function would pull zod and the whole schema graph into an operator console.
 *
 * `emailLayout.test.ts` holds the duplication honest by importing the contract **by source
 * path** (a test runs in node, so it can hold the real thing against the copy) and asserting
 * that this file's verdict matches the contract's on every input it is given. A rule edited on
 * one side and not the other fails the build rather than reaching an operator.
 *
 * ## What the renderer is for
 *
 * The API's `preview` route renders the **stored** template. That is the right authority and
 * the console uses it wherever it can. It cannot help with the thing an operator is actually
 * looking at, which is an unsaved draft: a template now stores a body, so previewing a draft
 * without the chrome shows a bare fragment on a white page, which is not what anybody receives.
 * So the console wraps the draft the same way the server would. The output is close, not
 * identical, and it is never what gets sent.
 */
import type {
  EmailLayoutContent,
  EmailLink,
  EmailPaletteField,
  PlatformSettings,
} from '../contracts/types.js';
import { WCAG_AA_TEXT, contrastRatio, normaliseExternalUrl, normaliseHex } from './bannerContrast.js';

/**
 * The palette a deployment starts on. Mirrors `DEFAULT_EMAIL_PALETTE`.
 *
 * Every pair clears 4.5:1, and the test pins that against the contract's own checker. Note
 * `mutedTextColor` is `#667085` rather than a paler grey: the value this replaced measured
 * 2.5:1 on white, and it was the colour the footer and every "if you did not request this"
 * line was painted in.
 */
export const DEFAULT_EMAIL_PALETTE = {
  pageBackground: '#F2F4F7',
  cardBackground: '#FFFFFF',
  headerBackground: '#5B2CE6',
  headingColor: '#101828',
  bodyTextColor: '#475467',
  mutedTextColor: '#667085',
  linkColor: '#5B2CE6',
  buttonBackground: '#5B2CE6',
  buttonTextColor: '#FFFFFF',
  footerBackground: '#F2F4F7',
  footerTextColor: '#667085',
} as const satisfies Record<EmailPaletteField, string>;

export const DEFAULT_EMAIL_FOOTER_TEXT =
  'You are receiving this email because you have an account with us.';

export const EMAIL_LOGO_MIN_WIDTH = 60;
export const EMAIL_LOGO_MAX_WIDTH = 320;
export const DEFAULT_EMAIL_LOGO_WIDTH = 132;

/** What the editor opens on when the layout row has never been written. Mirrors the contract. */
export function defaultEmailLayoutContent(): EmailLayoutContent {
  return {
    senderName: '',
    replyTo: null,
    showLogo: true,
    logoWidthPx: DEFAULT_EMAIL_LOGO_WIDTH,
    ...DEFAULT_EMAIL_PALETTE,
    footerText: DEFAULT_EMAIL_FOOTER_TEXT,
    footerLegalText: '',
    socialLinks: [],
    legalLinks: [],
  };
}

// ── The palette rule ───────────────────────────────────────────────────────────

/** Just the colours, so the check runs on a half-filled form as well as a whole layout. */
export type EmailPalette = Readonly<Record<EmailPaletteField, string>>;

interface ContrastPair {
  readonly field: EmailPaletteField;
  readonly label: string;
  readonly against: EmailPaletteField;
  readonly againstLabel: string;
}

/**
 * Every pair in the layout where one colour is text drawn on another. Mirrors
 * `EMAIL_CONTRAST_PAIRS`.
 *
 * Written out rather than inferred, because the interesting part is *which background a colour
 * is actually painted on*, and that is a fact about the document the renderer builds rather than
 * something derivable from the names. `linkColor` appears twice for exactly that reason: links
 * occur in the card body and again in the footer, the two have different backgrounds, and a link
 * colour that reads on one can be invisible on the other.
 */
const CONTRAST_PAIRS: readonly ContrastPair[] = [
  { field: 'headingColor', label: 'heading', against: 'cardBackground', againstLabel: 'the card' },
  { field: 'bodyTextColor', label: 'body text', against: 'cardBackground', againstLabel: 'the card' },
  { field: 'mutedTextColor', label: 'small print', against: 'cardBackground', againstLabel: 'the card' },
  { field: 'linkColor', label: 'link', against: 'cardBackground', againstLabel: 'the card' },
  { field: 'linkColor', label: 'link', against: 'footerBackground', againstLabel: 'the footer' },
  { field: 'buttonTextColor', label: 'button label', against: 'buttonBackground', againstLabel: 'the button' },
  { field: 'footerTextColor', label: 'footer text', against: 'footerBackground', againstLabel: 'the footer' },
];

export interface EmailPaletteFailure {
  readonly field: EmailPaletteField;
  readonly required: number;
  readonly actual: number | null;
  readonly message: string;
}

export interface EmailPaletteVerdict {
  readonly ok: boolean;
  readonly failures: readonly EmailPaletteFailure[];
  /** Every ratio, so the form can show how close it is rather than only yes or no. */
  readonly ratios: readonly { readonly field: EmailPaletteField; readonly label: string; readonly ratio: number | null }[];
}

function round(value: number | null): number | null {
  return value === null ? null : Math.round(value * 10) / 10;
}

/**
 * Every pair a reader could not read, rather than the first.
 *
 * All of them, so an operator who picked one bad colour that is used twice fixes it once instead
 * of discovering the second occurrence only after correcting the first.
 */
export function checkEmailPalette(content: EmailPalette): EmailPaletteVerdict {
  const failures: EmailPaletteFailure[] = [];
  const ratios: { field: EmailPaletteField; label: string; ratio: number | null }[] = [];

  for (const pair of CONTRAST_PAIRS) {
    const foreground = content[pair.field];
    const background = content[pair.against];
    const ratio = contrastRatio(foreground, background);
    ratios.push({ field: pair.field, label: `${pair.label} on ${pair.againstLabel}`, ratio: round(ratio) });

    if (ratio === null) {
      failures.push({
        field: pair.field,
        required: WCAG_AA_TEXT,
        actual: null,
        message: 'That is not a readable colour. Use a six digit hex value like #101828.',
      });
      continue;
    }
    if (ratio < WCAG_AA_TEXT) {
      failures.push({
        field: pair.field,
        required: WCAG_AA_TEXT,
        actual: round(ratio),
        message: `The ${pair.label} is too close in tone to ${pair.againstLabel} to read. It needs ${String(WCAG_AA_TEXT)} to 1 contrast and this pair is ${String(round(ratio))} to 1.`,
      });
    }
  }

  return { ok: failures.length === 0, failures, ratios };
}

/** The palette fields, in the order the editor lists them, with the operator's own words. */
export const PALETTE_FIELDS: readonly { field: EmailPaletteField; label: string; hint: string }[] = [
  { field: 'pageBackground', label: 'Page', hint: 'Behind the whole message' },
  { field: 'cardBackground', label: 'Card', hint: 'The box the message sits in' },
  { field: 'headerBackground', label: 'Header band', hint: 'Behind the logo' },
  { field: 'headingColor', label: 'Headings', hint: 'Drawn on the card' },
  { field: 'bodyTextColor', label: 'Body text', hint: 'Drawn on the card' },
  { field: 'mutedTextColor', label: 'Small print', hint: 'Drawn on the card' },
  { field: 'linkColor', label: 'Links', hint: 'Drawn on the card and on the footer' },
  { field: 'buttonBackground', label: 'Button', hint: 'The call to action' },
  { field: 'buttonTextColor', label: 'Button label', hint: 'Drawn on the button' },
  { field: 'footerBackground', label: 'Footer', hint: 'Behind the small print at the bottom' },
  { field: 'footerTextColor', label: 'Footer text', hint: 'Drawn on the footer' },
];

// ── Links ──────────────────────────────────────────────────────────────────────

export type EmailLinkResult = { readonly link: EmailLink } | { readonly error: string };

/**
 * Re-word a refusal for somebody editing an email.
 *
 * `normaliseExternalUrl` is the right rule and the wrong sentence here: it explains itself in
 * terms of the mobile app, because that is where it was written. Somebody following a footer
 * link is in a mail client, not the app, so the consequence it names is one that will not happen
 * and the one that will is left unsaid. The rule is untouched and still the single authority;
 * only the sentence is local.
 */
function emailWording(error: string): string {
  if (error.includes('https addresses are accepted')) {
    return 'Links must start with https. A plain http link in a message sent to everybody is one a network attacker can rewrite for all of them at once.';
  }
  return error;
}

/**
 * Validate one footer or social link.
 *
 * `https` only, parsed rather than pattern-matched, and no credentials in the authority. A
 * footer link is clicked out of an email carrying the platform's name, so `http` there is the
 * platform downgrading its own customers onto a plaintext connection, and
 * `https://user:pass@host` is the display spoof that makes a hostile host look like a trusted
 * one. `normaliseExternalUrl` is the same function the banner editor and the app both use, so a
 * destination refused here is refused everywhere.
 */
export function normaliseEmailLink(raw: { label: string; url: string }): EmailLinkResult {
  const label = raw.label.trim();
  if (label === '') return { error: 'Give the link a label, or remove the row.' };
  if (label.length > 40) return { error: 'Keep the label under 40 characters.' };
  const url = normaliseExternalUrl(raw.url);
  if ('error' in url) return { error: emailWording(url.error) };
  return { link: { label, url: url.url } };
}

/**
 * Validate a whole set of link rows.
 *
 * Returns every problem at once with its row number, so an operator who typed two bad addresses
 * fixes both in one pass. A wholly empty row is somebody who clicked Add and changed their mind,
 * not an error.
 */
export function normaliseEmailLinks(
  rows: readonly { label: string; url: string }[],
  max: number,
  what: string,
): { links: readonly EmailLink[] } | { errors: readonly string[] } {
  if (rows.length > max) {
    return { errors: [`A message carries at most ${String(max)} ${what}.`] };
  }
  const links: EmailLink[] = [];
  const errors: string[] = [];
  rows.forEach((row, index) => {
    if (row.label.trim() === '' && row.url.trim() === '') return;
    const result = normaliseEmailLink(row);
    if ('error' in result) errors.push(`${what} ${String(index + 1)}: ${result.error}`);
    else links.push(result.link);
  });
  return errors.length > 0 ? { errors } : { links };
}

// ── Tokens ─────────────────────────────────────────────────────────────────────

/** Escape a value that is about to be interpolated into HTML. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Every token the server fills on a render: the six from `platform_settings` and the seven from
 * the active layout's palette.
 *
 * The layout seven exist because the shipped bodies used to write `color:#101828` into every
 * heading, so an operator who chose a dark layout got dark text on a dark card and nothing in
 * the system noticed. The palette validator guards the layout's own pairs; it cannot guard a hex
 * literal typed into a body. Exposing the palette as tokens is what makes the validator cover
 * the whole message.
 */
export function emailTokens(
  settings: PlatformSettings,
  layout: EmailLayoutContent,
  logoUrl: string | null,
): Record<string, string> {
  return {
    logo: logoUrl ?? 'https://dummyimage.com/160x36/5b2ce6/ffffff.png&text=Logo',
    platformName: settings.platformName,
    primaryColor: settings.primaryColor,
    accentColor: settings.accentColor,
    supportEmail: settings.supportEmail,
    websiteUrl: settings.websiteUrl ?? '#',
    layoutHeading: layout.headingColor,
    layoutBody: layout.bodyTextColor,
    layoutMuted: layout.mutedTextColor,
    layoutLink: layout.linkColor,
    layoutButtonBg: layout.buttonBackground,
    layoutButtonText: layout.buttonTextColor,
    layoutCardBg: layout.cardBackground,
  };
}

// ── The preview renderer ───────────────────────────────────────────────────────

const FONT_STACK =
  "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const FRAME_WIDTH = 600;

/** Only a colour reaches a style attribute. Anything else falls back rather than being written. */
function colour(value: string, fallback: string): string {
  return normaliseHex(value) ?? fallback;
}

/** Only `https`, `mailto:` and the empty string reach an href. */
function safeUrl(value: string | null): string {
  if (value === null) return '';
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (trimmed.startsWith('mailto:')) return escapeHtml(trimmed);
  const parsed = normaliseExternalUrl(trimmed);
  return 'error' in parsed ? '' : escapeHtml(parsed.url);
}

function linkRow(links: readonly EmailLink[], color: string, size: number): string {
  const rendered = links
    .map((link) => {
      const href = safeUrl(link.url);
      const label = escapeHtml(link.label.trim());
      if (href === '' || label === '') return null;
      return `<a href="${href}" style="color:${color};text-decoration:underline;">${label}</a>`;
    })
    .filter((entry): entry is string => entry !== null);
  if (rendered.length === 0) return '';
  return `<p style="margin:0 0 10px 0;font-size:${String(size)}px;line-height:20px;">${rendered.join(' &middot; ')}</p>`;
}

export interface LayoutBrand {
  readonly platformName: string;
  readonly logoUrl: string | null;
  readonly supportEmail: string;
  readonly websiteUrl: string | null;
}

/**
 * Wrap a template body in the branded chrome, the way the server does.
 *
 * Pure: a layout, a brand and a body in, a document out. No network, no clock. Mirrors
 * `renderWithLayout` closely enough for an operator to judge a draft by, and is never what gets
 * sent: the API renders the stored template, and this renders the one on screen.
 *
 * The preheader block is two parts, and the second is the one people forget. The hidden div
 * carries the text; the run of zero-width characters after it stops the client continuing into
 * the body, without which an inbox shows the preheader followed by the first words of the
 * message, which is worse than no preheader at all.
 */
export function renderWithLayout(
  layout: EmailLayoutContent,
  brand: LayoutBrand,
  input: { readonly preheader: string; readonly bodyHtml: string },
): string {
  const page = colour(layout.pageBackground, DEFAULT_EMAIL_PALETTE.pageBackground);
  const card = colour(layout.cardBackground, DEFAULT_EMAIL_PALETTE.cardBackground);
  const header = colour(layout.headerBackground, DEFAULT_EMAIL_PALETTE.headerBackground);
  const body = colour(layout.bodyTextColor, DEFAULT_EMAIL_PALETTE.bodyTextColor);
  const footerBg = colour(layout.footerBackground, DEFAULT_EMAIL_PALETTE.footerBackground);
  const footerFg = colour(layout.footerTextColor, DEFAULT_EMAIL_PALETTE.footerTextColor);
  const link = colour(layout.linkColor, DEFAULT_EMAIL_PALETTE.linkColor);
  const buttonText = colour(layout.buttonTextColor, DEFAULT_EMAIL_PALETTE.buttonTextColor);
  const name = escapeHtml(brand.platformName);

  const preheaderText = input.preheader.trim();
  const preheader =
    preheaderText === ''
      ? ''
      : `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:transparent;opacity:0;">${escapeHtml(preheaderText)}${'&#8203;&#847;&nbsp;'.repeat(30)}</div>`;

  const site = safeUrl(brand.websiteUrl);
  const logoSrc = layout.showLogo ? safeUrl(brand.logoUrl) : '';
  const mark =
    logoSrc === ''
      ? `<span style="font-family:${FONT_STACK};font-size:22px;line-height:28px;font-weight:700;color:${buttonText};">${name}</span>`
      : `<img src="${logoSrc}" alt="${name}" width="${String(layout.logoWidthPx)}" style="width:${String(layout.logoWidthPx)}px;max-width:${String(layout.logoWidthPx)}px;height:auto;display:block;border:0;">`;
  const logo = site === '' ? mark : `<a href="${site}" style="text-decoration:none;">${mark}</a>`;

  const footerParts: string[] = [];
  const footerText = layout.footerText.trim();
  if (footerText !== '') {
    footerParts.push(`<p style="margin:0 0 10px 0;font-size:13px;line-height:20px;">${escapeHtml(footerText)}</p>`);
  }
  const support = brand.supportEmail.trim();
  if (support !== '') {
    footerParts.push(
      `<p style="margin:0 0 10px 0;font-size:13px;line-height:20px;">Need help? Contact <a href="mailto:${escapeHtml(support)}" style="color:${link};text-decoration:underline;">${escapeHtml(support)}</a></p>`,
    );
  }
  footerParts.push(linkRow(layout.socialLinks, link, 13));
  footerParts.push(linkRow(layout.legalLinks, link, 12));
  const legal = layout.footerLegalText.trim();
  if (legal !== '') {
    footerParts.push(`<p style="margin:10px 0 0 0;font-size:12px;line-height:18px;">${escapeHtml(legal)}</p>`);
  }

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<title>${name}</title>
</head>
<body style="margin:0;padding:0;width:100%;background-color:${page};" bgcolor="${page}">
${preheader}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${page};">
  <tr>
    <td align="center" style="padding:24px 12px;">
      <table role="presentation" width="${String(FRAME_WIDTH)}" cellpadding="0" cellspacing="0" border="0" style="width:${String(FRAME_WIDTH)}px;max-width:${String(FRAME_WIDTH)}px;">
        <tr>
          <td align="center" style="background-color:${header};padding:28px 32px;border-radius:14px 14px 0 0;">
            ${logo}
          </td>
        </tr>
        <tr>
          <td style="background-color:${card};padding:36px 32px;border-radius:0 0 14px 14px;font-family:${FONT_STACK};font-size:15px;line-height:24px;color:${body};">
            ${input.bodyHtml}
          </td>
        </tr>
        <tr>
          <td align="center" style="background-color:${footerBg};padding:24px 32px;font-family:${FONT_STACK};color:${footerFg};">
            ${footerParts.filter((part) => part !== '').join('\n            ')}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}
